import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import "./TaskNotifications.scss";

const MAX_VISIBLE_TOASTS = 4;
const AUTO_CLOSE_MS = 7000;

const getToastKind = (type) => {
  if (type === "completed") return "success";
  if (type === "error" || type === "failed") return "error";
  return "info";
};

const formatCreatedAt = (isoDate) => {
  if (!isoDate) return "";
  try {
    return new Date(isoDate).toLocaleString("ru-RU");
  } catch {
    return isoDate;
  }
};

const TaskNotifications = ({ user, notifications, isLoading, pollError, markNotificationsSeen, seenIds }) => {
  const [toasts, setToasts] = useState([]);
  const shownIdsRef = useRef(new Set());
  const timersRef = useRef(new Map());

  const userId = useMemo(() => user?.id ?? user?.id_user, [user]);

  useEffect(() => {
    shownIdsRef.current = new Set();
    setToasts([]);
  }, [userId]);

  useEffect(() => {
    const closeToast = (notificationId) => {
      setToasts((prev) => prev.filter((item) => item.notification_id !== notificationId));
      const timeoutId = timersRef.current.get(notificationId);
      if (timeoutId) {
        window.clearTimeout(timeoutId);
        timersRef.current.delete(notificationId);
      }
    };

    const queueToast = (notification) => {
      const notificationId = notification.notification_id;
      if (!notificationId || shownIdsRef.current.has(notificationId)) return;
      shownIdsRef.current.add(notificationId);
      if (markNotificationsSeen) markNotificationsSeen(notificationId);

      setToasts((prev) => {
        const next = [notification, ...prev];
        return next.slice(0, MAX_VISIBLE_TOASTS);
      });

      const timeoutId = window.setTimeout(() => {
        closeToast(notificationId);
      }, AUTO_CLOSE_MS);
      timersRef.current.set(notificationId, timeoutId);
    };

    const unread = (notifications || []).filter((item) => {
      if (item?.read !== false) return false;
      const id = item?.notification_id;
      if (!id) return false;
      return !(seenIds instanceof Set ? seenIds.has(String(id)) : false);
    });
    unread.forEach(queueToast);
  }, [notifications, userId, seenIds]);

  useEffect(() => {
    return () => {
      timersRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      timersRef.current.clear();
    };
  }, []);

  if (!userId) return null;

  return (
    <div className="task-notifications">
      <div className="task-notifications__stack" aria-live="polite" aria-atomic="false">
        {toasts.map((item) => {
          const kind = getToastKind(item.type);
          return (
            <div
              key={item.notification_id}
              className={`task-notifications__toast task-notifications__toast--${kind}`}
              role="status"
            >
              <div className="task-notifications__header">
                <span className="task-notifications__type">
                  {kind === "success" ? "Завершено" : kind === "error" ? "Ошибка" : "Событие"}
                </span>
                <button
                  type="button"
                  className="task-notifications__close"
                  onClick={() =>
                    setToasts((prev) => prev.filter((x) => x.notification_id !== item.notification_id))
                  }
                  aria-label="Закрыть уведомление"
                >
                  ×
                </button>
              </div>
              <div className="task-notifications__message">{item.message}</div>
              <div className="task-notifications__meta">
                <span>{formatCreatedAt(item.created_at)}</span>
                {item.task_id ? <span>ID: {item.task_id}</span> : null}
              </div>
            </div>
          );
        })}
      </div>

      {(isLoading || pollError) && (
        <div className="task-notifications__status">
          {pollError ? pollError : "Проверяем уведомления..."}
        </div>
      )}
    </div>
  );
};

TaskNotifications.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    id_user: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  notifications: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  pollError: PropTypes.string,
  markNotificationsSeen: PropTypes.func,
  seenIds: PropTypes.instanceOf(Set),
};

TaskNotifications.defaultProps = {
  user: null,
  notifications: [],
  isLoading: false,
  pollError: null,
  markNotificationsSeen: null,
  seenIds: new Set(),
};

export default TaskNotifications;

